import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiErrorDetail } from '@/lib/apiError'
import { documentTypeLabel, uploadableDocumentTypes, useUploadDocument } from '@/models/document'
import { useCurrentUser } from '@/models/user'

const ACCEPT = '.pdf,.xlsx,.xls,.csv,.tsv,.txt,.docx,.doc,.png,.jpg,.jpeg,.gif,.webp'

/** `folder` is the vault's; the Documents list uploads unfiled and self-files by type. */
export function UploadDocumentDialog({
  orgId,
  folder = null,
  folderName = null,
  onClose,
}: {
  orgId: string
  folder?: string | null
  folderName?: string | null
  onClose: () => void
}) {
  const { user } = useCurrentUser({ enabled: true })
  const types = uploadableDocumentTypes(!!user?.isCaiAdmin)
  const [documentType, setDocumentType] = useState<string>(types[0])
  const [file, setFile] = useState<File | null>(null)
  const upload = useUploadDocument(orgId)

  const submit = () => {
    if (!file) return
    upload.mutate(
      { file, documentType, folder },
      {
        onSuccess: () => {
          toast.success('Document uploaded.')
          onClose()
        },
        onError: (error) => toast.error(apiErrorDetail(error, 'Could not upload that file.')),
      }
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{folderName ? `Upload to ${folderName}` : 'Upload document'}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="vault-upload-type">Type</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger id="vault-upload-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {types.map((type) => (
                  <SelectItem key={type} value={type}>
                    {documentTypeLabel(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label>File</Label>
            <Dropzone
              onChange={setFile}
              accept={ACCEPT}
              maxSizeMB={10}
              disabled={upload.isPending}
              isLoading={upload.isPending}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={upload.isPending ? 'Uploading…' : undefined} disabled={!file} onClick={submit}>
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
